/**
 * Admin page: Hardware Presets
 * Shared team-wide library of quick-add items for the Contract Pack wizard
 * (Zappi chargers, bird mesh, isolators, etc.). Any admin can add/edit/delete.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Plus, Trash2, Pencil, Loader2, Package, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { CsvImportDialog } from '../components/CsvImportDialog';

const empty = { category: 'Additional Hardware', description: '', qty: 1, unit_price: '', warranty_years: '' };

export const HardwarePresetsContent = ({ embedded = false }) => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | {id, ...}
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/hardware-presets');
      setPresets(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const save = async () => {
    if (!editing.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category: editing.category || 'Additional Hardware',
        description: editing.description.trim(),
        qty: parseInt(editing.qty || 1, 10) || 1,
        unit_price: parseFloat(editing.unit_price || 0) || 0,
        warranty_years: editing.warranty_years ? parseInt(editing.warranty_years, 10) : null,
      };
      if (editing.id) {
        await api.put(`/hardware-presets/${editing.id}`, payload);
        toast.success('Preset updated');
      } else {
        await api.post('/hardware-presets', payload);
        toast.success('Preset added');
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (id, description) => {
    if (!window.confirm(`Delete preset "${description}"?`)) return;
    try {
      await api.delete(`/hardware-presets/${id}`);
      toast.success('Deleted');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="hardware-presets-page">
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package className="w-6 h-6 text-violet-600" />
              Hardware Presets
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Shared library of quick-add hardware items for the Contract Pack wizard.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import-csv-btn">
              <Upload className="w-4 h-4 mr-1" /> Import CSV
            </Button>
            <Button
              onClick={() => setEditing({ ...empty })}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="add-preset-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Preset
            </Button>
          </div>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end gap-2 mb-3">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import-csv-btn" size="sm">
            <Upload className="w-4 h-4 mr-1" /> Import CSV
          </Button>
          <Button onClick={() => setEditing({ ...empty })} className="bg-violet-600 hover:bg-violet-700" data-testid="add-preset-btn" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Preset
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Presets ({presets.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : presets.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-6 text-center">
              No presets yet. Click "Add Preset" to create your first quick-add item.
            </p>
          ) : (
            presets.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-2 p-3 rounded border border-slate-200 hover:bg-slate-50"
                data-testid={`preset-${p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] border-violet-200 text-violet-700">{p.category}</Badge>
                    <span className="font-medium text-sm text-slate-800">{p.description}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Qty: {p.qty} · Unit £{Number(p.unit_price || 0).toFixed(2)}
                    {p.warranty_years ? ` · ${p.warranty_years} yr warranty` : ''}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...p })} className="h-8 text-xs" data-testid={`edit-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id, p.description)} className="h-8 text-xs text-rose-600 hover:bg-rose-50" data-testid={`delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="preset-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Preset' : 'Add Preset'}</DialogTitle>
            <DialogDescription>
              Save common bolt-on items so they can be added to a contract with one click.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="EV Charger / Bird Protection / etc." data-testid="preset-category" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="MyEnergi Zappi v2.1 (Black, 7kW tethered)" data-testid="preset-description" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Default Qty</Label>
                  <Input type="number" min="1" value={editing.qty} onChange={(e) => setEditing({ ...editing, qty: e.target.value })} data-testid="preset-qty" />
                </div>
                <div>
                  <Label className="text-xs">Unit Price (£)</Label>
                  <Input type="number" step="0.01" value={editing.unit_price} onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })} data-testid="preset-price" />
                </div>
                <div>
                  <Label className="text-xs">Warranty (yrs)</Label>
                  <Input type="number" min="0" value={editing.warranty_years || ''} onChange={(e) => setEditing({ ...editing, warranty_years: e.target.value })} data-testid="preset-warranty" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="preset-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing?.id ? 'Update' : 'Add'} Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Import Hardware Presets from CSV"
        description="Bulk load quick-add hardware items. Rows are upserted by category + description."
        templateUrl="/hardware-presets/template.csv"
        importUrl="/hardware-presets/import-csv"
        templateFilename="hardware-presets-template.csv"
        onSuccess={reload}
        colorClass="bg-violet-600 hover:bg-violet-700"
        iconColor="text-violet-600"
      />
    </div>
  );
};

export default function HardwarePresets() {
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <HardwarePresetsContent />
    </div>
  );
}
